"use client";

import Navbar from "../../components/Layout/Navbar";
import Footer from "../../components/Layout/Footer";
import PageMeta from "../../components/pagesHelmet/PageMeta";

const BRAND = {
  primary: "#3B82F6",
  primaryDark: "#2563EB",
  primarySoft: "rgba(59, 130, 246, 0.1)",
  muted: "#6B7280",
};

export default function RefundPolicyPage() {
  return (
    <>
    <PageMeta 
      title="Refund Policy | U2LR URL Shortener" 
      description="Learn about U2LR's refund policy for subscription plans, ensuring transparency and customer satisfaction for all our services." 
    />
      <Navbar />

      <div className="bg-white">
        {/* ================= HERO ================= */}
        <section
          className="text-white"
          style={{
            background: `linear-gradient(135deg, #000, #4b76d1)`,
          }}
        >
          <div className="container py-5">
            <div className="row justify-content-center text-center">
              <div className="col-lg-8 py-5">
                <h1 className="display-6 fw-bold mb-3">
                  Refund & Dispute Policy
                </h1>
                <p className="opacity-75 mb-0">
                  This policy explains how refunds, cancellations, and payment
                  disputes are handled on u2lr.
                </p>
                <small className="d-block mt-3 opacity-75">
                  Effective Date: December 2, 2025
                </small>
              </div>
            </div>
          </div>
        </section>

        {/* ================= CONTENT ================= */}
        <section className="py-5">
          <div className="container py-4">
            <div className="row justify-content-center">
              <div className="col-lg-9">
                <PolicySection title="1. Nature of the Service">
                  <p>
                    u2lr.com provides digital, subscription-based services for
                    link shortening, tracking, and analytics.
                  </p>
                  <p>
                    All services are intangible and delivered immediately upon
                    successful payment. As access to paid features is granted
                    instantly, all sales are considered final except where
                    required by law or explicitly stated in this policy.
                  </p>
                </PolicySection>

                <PolicySection title="2. Free Plan & Evaluation">
                  <p>
                    u2lr offers a free plan that allows users to evaluate the
                    platform before purchasing a paid subscription.
                  </p>
                  <p>
                    Users are responsible for reviewing features, limitations,
                    and suitability before upgrading to a paid plan.
                  </p>
                </PolicySection>

                <PolicySection title="3. Refund Eligibility">
                  <p>Refunds are granted only under the following limited circumstances:</p>
                  <ul>
                    <li>Duplicate charges or confirmed billing errors</li>
                    <li>
                      Complete service unavailability caused solely by u2lr for
                      a significant duration after payment, where the issue could
                      not be resolved after support review
                    </li>
                  </ul>
                  <p>
                    Refund requests must be submitted within <strong>7 days</strong> of
                    the original transaction date.
                  </p>
                </PolicySection>

                <PolicySection title="4. Non-Refundable Situations">
                  <p>Refunds will not be issued for the following:</p>
                  <ul>
                    <li>Change of mind after purchase</li>
                    <li>Failure to use the service</li>
                    <li>Forgetting to cancel before renewal</li>
                    <li>Partial use of a subscription period</li>
                    <li>
                      Dissatisfaction with results, traffic quality, or analytics data
                    </li>
                    <li>
                      Account suspension or termination due to violations of our
                      Terms of Service
                    </li>
                    <li>
                      Payments made for features that were successfully delivered
                    </li>
                  </ul>
                  <p className="fw-semibold">
                    Once a subscription period begins, it is deemed used and
                    non-refundable.
                  </p>
                </PolicySection>

                <PolicySection title="5. Subscription Cancellation">
                  <p>
                    Users may cancel their subscription at any time through the
                    account dashboard.
                  </p>
                  <ul>
                    <li>Cancellation stops future billing only</li>
                    <li>No refunds are issued for the current billing period</li>
                    <li>
                      Access remains active until the end of the paid subscription
                      cycle
                    </li>
                  </ul>
                </PolicySection>

                <PolicySection title="6. Chargebacks & Payment Disputes">
                  <p>
                    Before filing a chargeback or payment dispute with Stripe,
                    PayPal, or any payment provider, users must contact u2lr
                    support to allow us to review and resolve the issue.
                  </p>
                  <p>
                    Unauthorized chargebacks may result in account suspension,
                    service termination, or permanent restriction from future
                    use.
                  </p>
                  <p>
                    For dispute resolution, u2lr may submit evidence including:
                  </p>
                  <ul>
                    <li>Proof of account ownership</li>
                    <li>Login and activity logs</li>
                    <li>Feature access records</li>
                    <li>IP address and timestamp data</li>
                    <li>Acceptance of Terms and Policies</li>
                  </ul>
                </PolicySection>

                <PolicySection title="7. How to Request a Refund">
                  <p>
                    Eligible refund requests must be submitted via email to:
                  </p>
                  <p className="fw-semibold mb-1">📧 support@u2lr.com</p>
                  <p>Please include the following details:</p>
                  <ul>
                    <li>Account email address</li>
                    <li>Payment date</li>
                    <li>Transaction ID</li>
                    <li>Clear reason for the request</li>
                  </ul>
                  <p>
                    Requests are reviewed within 2–3 business days. Approval is
                    at the sole discretion of u2lr, in accordance with this
                    policy.
                  </p>
                </PolicySection>

                <PolicySection title="8. Policy Updates">
                  <p>
                    u2lr reserves the right to update this Refund & Dispute Policy
                    at any time. Continued use of the service constitutes
                    acceptance of any changes.
                  </p>
                </PolicySection>
              </div>
            </div>
          </div>
        </section>

        {/* ================= FOOT NOTE ================= */}
        <section
          className="text-center py-5"
          style={{ backgroundColor: BRAND.primarySoft }}
        >
          <div className="container">
            <p className="mb-0 text-muted">
              u2lr operates on transparent billing and fair usage principles.
            </p>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

/* ================= HELPER ================= */

function PolicySection({ title, children }) {
  return (
    <div className="mb-5">
      <h4 className="fw-bold mb-3">{title}</h4>
      <div className="text-muted fs-6">{children}</div>
    </div>
  );
}
